import { useCallback, useEffect, useState } from 'react';

import { requestPage } from './api';

/**
 * Лента с курсорной подгрузкой (T11.10).
 *
 * Лент в приложении три: выдача поиска, избранное и история
 * уведомлений. Порядок у всех один — первая страница заменяет список,
 * следующая дополняет, отказ на второй странице не стирает первую, — и
 * три его копии разошлись бы: где-то «показать ещё» затирало бы
 * просмотренное, где-то отказ сети оставлял бы пустой экран вместо
 * загруженного.
 *
 * ## Кнопка, а не бесконечная прокрутка
 *
 * Бесконечная прокрутка отнимает у человека конец списка: он не знает,
 * просмотрел ли всё. Здесь это особенно неверно — он ищет одну машину и
 * хочет убедиться, что не пропустил её.
 *
 * ## Удаление правит список на месте
 *
 * `drop` убирает строку, которую сервер уже удалил, не перезагружая
 * ленту. Перезагрузка вернула бы человека в начало и подтянула бы
 * страницу заново ради одной исчезнувшей строки.
 */

export type PageState = 'loading' | 'ready' | 'more';

export interface Page<T> {
  readonly items: readonly T[];
  readonly state: PageState;
  /** Отказ последнего запроса; пусто — отказа не было. */
  readonly error: unknown;
  readonly hasMore: boolean;
  readonly more: () => void;
  readonly reload: () => void;
  readonly drop: (gone: (item: T) => boolean) => void;
  /** Заменить строку на месте: отметка прочтения меняет одну запись. */
  readonly replace: (change: (item: T) => T) => void;
}

/**
 * Адрес страницы.
 *
 * Предел и курсор дописываются здесь, а не вызывающим: три места, где
 * это делается вручную, — три возможности написать `cursor` через
 * `?` вместо `&` и получить страницу без продолжения.
 */
function at(path: string, limit: number, from: string | null): string {
  const sign = path.includes('?') ? '&' : '?';
  const cursor = from === null ? '' : `&cursor=${encodeURIComponent(from)}`;

  return `${path}${sign}limit=${String(limit)}${cursor}`;
}

export function usePage<T>(path: string, limit: number): Page<T> {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [state, setState] = useState<PageState>('loading');
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const load = useCallback(
    async (from: string | null) => {
      setError(null);
      setState(from === null ? 'loading' : 'more');

      try {
        const page = await requestPage<T>(at(path, limit, from));

        // Первая страница заменяет список, следующая дополняет: иначе
        // «показать ещё» стирало бы то, что человек уже просмотрел
        setItems((current) => (from === null ? page.items : [...current, ...page.items]));
        setCursor(page.cursor);
        setHasMore(page.hasMore);
      } catch (failure: unknown) {
        setError(failure);
      } finally {
        setState('ready');
      }
    },
    [limit, path],
  );

  // Смена адреса — новая лента с начала: продолжить прежним курсором
  // значит дополнить список записями по старым условиям
  useEffect(() => {
    void load(null);
  }, [attempt, load]);

  return {
    items,
    state,
    error,
    hasMore,
    more: useCallback(() => {
      void load(cursor);
    }, [cursor, load]),
    reload: useCallback(() => setAttempt((one) => one + 1), []),
    drop: useCallback((gone: (item: T) => boolean) => {
      setItems((current) => current.filter((one) => !gone(one)));
    }, []),
    replace: useCallback((change: (item: T) => T) => {
      setItems((current) => current.map(change));
    }, []),
  };
}
